<template>
    <section
        class="section"
        :class="sectionClass"
        ref="videoSectionElement"
    >
        <div v-if="loading && !isAudio" class="loader" />
        <video
            class="video"
            :class="videoClass"
            :src="src"
            ref="playerElement"
            :controls="false"
            :poster="poster"
        />
        <div
            class="controls"
            :class="controlsClass"
            @click.stop="() => {}"
        >
            <div class="play-pause" @click="togglePlay">
                <v-icon color="white">{{ playIcon }}</v-icon>
            </div>
            <div class="timeline" ref="timelineElement">
                <div
                    class="current"
                    ref="currentTimelineElement"
                />
                <div
                    class="buffer"
                    ref="bufferTimelineElement"
                />
                <div class="timer">
                    {{ timer }}
                </div>


            </div>
            <div class="settings">
                <div
                    class="button volume-button"
                    @click="toggleMute"
                    @mouseenter="showVolumeSlider"
                    @mouseleave="hideVolumeSlider"
                >
                    <v-icon color="white">
                        {{ volumeIcon }}
                    </v-icon>
                    
                </div>
                <div 
                    class="volume-container"
                    v-show="isVolumeSliderVisible"
                    @mouseenter="isVolumeSliderHovered = true"
                    @mouseleave="isVolumeSliderHovered = false; hideVolumeSlider()"
                >
                    <div 
                        class="volume-slider" 
                        ref="volumeSliderElement"
                        @mousedown="setFocusOnVolume"
                    >
                        <div class="volume-current" ref="volume_current"></div>
                    </div>
                </div>
                <div
                    class="button playback-rate-button"
                    style="width: 58px;"
                    @mouseenter="showPlaybackRateMenu"
                    @mouseleave="hidePlaybackRateMenu"
                >
                    <v-icon color="white">
                        {{ playbackRateIcon }}
                    </v-icon>
                    <span class="playback-rate-label">{{ playbackRateLabel }}</span>
                    <div 
                        class="playback-rate-menu"
                        v-if="isPlaybackRateMenuVisible"
                        @mouseenter="isPlaybackRateMenuHovered = true"
                        @mouseleave="isPlaybackRateMenuHovered = false; hidePlaybackRateMenu()"
                    >
                        <div 
                            v-for="rate in playbackRates" 
                            :key="rate"
                            class="playback-rate-item"
                            :class="{ 'active': isActiveRate(rate) }"
                            @click="changePlaybackRate(rate)"
                        >
                            <span class="playback-rate-value">{{ `${rate}x` }}</span>
                            <v-icon 
                                v-if="isActiveRate(rate)" 
                                color="var(--bg)"
                                class="check-icon"
                            >
                                mdi-check
                            </v-icon>
                        </div>
                    </div>
                </div>
                <div v-if="!isAudio" class="button" @click="toggleFullscreen">
                    <v-icon color="white">
                        mdi-fullscreen
                    </v-icon>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import {secondsToHMS} from '@/assets/helpers/date.js'
export default {
    props: {
        src: {
            type: String,
            default: '',
        },
        poster: {
            type: String,
            default: '',
        },
        mimetype: {
            type: String,
            default: 'video/mp4',
        },
        filename: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            loading: true,
            playerElement: null,
            videoSectionElement: null,
            timelineElement: null,
            currentTimelineElement: null,
            bufferTimelineElement: null,
            volumeSliderElement: null,
            playerIsReady: false,
            duration: 0,
            volume: 100,
            previousVolume: 100,
            isControlShows: true,
            isPlaying: false,
            hideTimeout: null,
            currentTime: 0,
            focusOnTimeline: false,
            isPlayingBeforeDrag: false,
            isVolumeSliderVisible: false,
            focusOnVolume: false,
            volumeBeforeDrag: 100,
            isVolumeSliderHovered: false,
            volumeSliderTimeout: null,
            playbackRates: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
            currentPlaybackRate: 1,
            isPlaybackRateMenuVisible: false,
            isPlaybackRateMenuHovered: false,
            playbackRateMenuTimeout: null,
            fullscreenMode: false,
            isPlayRequestPending: false,
        }
    },
    computed: {
        playIcon() {
            return !this.isPlaying
                ? 'play_arrow'
                : 'pause'
        },
        volumeIcon() {
            if (this.volume === 0) return 'mdi-volume-mute'
            if (this.volume < 30) return 'mdi-volume-low'
            if (this.volume < 70) return 'mdi-volume-medium'
            return 'mdi-volume-high'
        },
        playbackRateIcon() {
            return 'mdi-speedometer';
        },
        playbackRateLabel() {
            return this.currentPlaybackRate === 1 ? '1x' : `${this.currentPlaybackRate}x`;
        },
        timer() {
            return secondsToHMS(this.currentTime) + '/' +  secondsToHMS(this.duration)
        },
        isAudio() {
            return this.mimetype.startsWith('audio')
        },
        sectionClass() {
            return [
                this.isControlShows ? 'section__cursor-show' : 'section__cursor-hide'
            ]
        },
        controlsClass() {
            return [
                this.isControlShows ? 'controls--show' : 'controls--hide',
                this.fullscreenMode && 'controls--fullscreen',
            ]
        },
        videoClass() {
            return [
                this.fullscreenMode && 'video--fullscreen',
            ]
        },
    },
    methods: {
        secondsToHMS,
        togglePlay() {
            if (this.isPlaying) {
                this.pause()
            } else {
                this.play();
            }
        },
        async play() {
            this.playerElement.style.objectFit = 'contain'
            if (this.playerElement.paused) {
                this.isPlayRequestPending = true;
                this.isPlaying = true;
                try {
                    const playPromise = this.playerElement.play();
                    if (playPromise !== undefined) {
                        await playPromise;
                        this.watchProgress();
                    }
                } catch (error) {
                    if (error.name !== 'AbortError') {
                        console.warn('Play failed:', error);
                    }
                    this.isPlaying = false;
                } finally {
                    this.isPlayRequestPending = false;
                }
            }
        },
        async pause() {
            if (!this.playerElement.paused) {
                try {
                    await this.playerElement.pause();
                } catch (error) {
                    console.warn('Pause failed:', error);
                }
            }
            this.isPlaying = false;
            this.isPlayRequestPending = false;
        },
        toggleVolumeSlider() {
            this.isVolumeSliderVisible = !this.isVolumeSliderVisible;
            if (this.isVolumeSliderVisible) {
                this.handleVolumeBar()
            }
        },
        showVolumeSlider() {
            clearTimeout(this.volumeSliderTimeout);
            this.isVolumeSliderVisible = true;
            this.handleVolumeBar()
        },
        hideVolumeSlider() {
            this.volumeSliderTimeout = setTimeout(() => {
                if (!this.isVolumeSliderHovered && !this.focusOnVolume) {
                    this.isVolumeSliderVisible = false;
                }
            }, 300);
        },
        handleVolumeBar() {
            if (this.$refs['volume_current']) {
                this.$refs['volume_current'].style.height = this.volume + "%";
            }
        },
        selectVolume(e) {
            if (!this.focusOnVolume) return
            if (!this.volumeSliderElement) return;
            const volumeSliderRect = this.volumeSliderElement.getBoundingClientRect();
            const height = Math.min(
                Math.max(0, volumeSliderRect.bottom - e.clientY),
                volumeSliderRect.height
            );
            const percentage = (height / volumeSliderRect.height) * 100;

            this.volume = Math.max(0, Math.min(100, Math.round(percentage)));
            this.setVolume();
        },
        setFocusOnVolume(e) {
            e.stopPropagation();
            this.volumeBeforeDrag = this.volume;
            this.focusOnVolume = true;
            this.selectVolume(e);
        },
        unsetFocusOnVolume() {
            this.focusOnVolume = false;
            if (!this.isVolumeSliderHovered) {
                setTimeout(() => {
                    this.isVolumeSliderVisible = false;
                }, 100);
            }
        },
        setVolume() {
            if (this.playerElement) {
                this.playerElement.volume = this.volume / 100;
            }
            this.handleVolumeBar();
        },
        toggleMute() {
            if (this.volume == 0) {
                this.volume = this.previousVolume || 50;
            } else {
                this.previousVolume = this.volume;
                this.volume = 0;
            }
            this.setVolume();
        },
        canInitPlayer() {
            this.playerIsReady = true
            this.setVolume();
            this.handleVolumeBar();
            this.duration = this.playerElement.duration
            this.loading = false
        },
        showControls() {
            this.isControlShows = true
            if (this.hideTimeout) {
                clearTimeout(this.hideTimeout);
            }
            if (this.isAudio) return
            this.hideTimeout = setTimeout(() => {
                if (this.isPlaying) {
                    this.isControlShows = false
                }
            }, 2000);
        },
        // hideControls() {
        //     if (this.hideTimeout) {
        //         clearTimeout(this.hideTimeout);
        //     }
        //     this.hideTimeout = setTimeout(() => {
        //         if (this.isPlaying) {
        //             this.isControlShows = false
        //         }
        //     }, 2000);
        // },
        handleProgress() {
            if (!this.playerElement.buffered || !this.playerElement.buffered.length) {
                return;
            }
            let width = this.playerElement.buffered.end(this.playerElement.buffered.length - 1) / this.playerElement.duration * 100 + .5
            if (width > 100) {
                width = 100
            }
            this.bufferTimelineElement.style.width = width + '%';
        },
        handleProgressBar() {
            this.currentTimelineElement.style.width = (this.playerElement.currentTime / this.playerElement.duration) * 100 + "%";
            this.currentTime = this.playerElement.currentTime
        },
        handleForward() {
            this.playerElement.currentTime += 5;
            this.handleProgressBar();
            this.showControls()
        },
        handlePlusVolume() {
            this.volume += 5
            if (this.volume > 100) {
                this.volume = 100
            }
            this.setVolume()
        },
        handleMinusVolume() {
            this.volume -= 5
            if (this.volume < 0) {
                this.volume = 0
            }
            this.setVolume()
        },
        handleBackward() {
            this.playerElement.currentTime -= 5;
            this.handleProgressBar();
            this.showControls()
        },
        handleKeypress(e) {
            const tagName = document.activeElement.tagName.toLowerCase();
            if (tagName === "input" || tagName === 'textarea') return;
            switch (e.key.toLowerCase()) {
                case " ":
                    e.preventDefault()
                    if (tagName === "button") return;
                    if (this.isPlaying) {
                    this.pause();
                    } else {
                    this.play();
                    }
                    break;
                case "f":
                    this.toggleFullscreen();
                    break;
                case "arrowright":
                    e.preventDefault()
                    this.handleForward();
                    break;
                case "arrowleft":
                    e.preventDefault()
                    this.handleBackward();
                    break;
                case "arrowup":
                    e.preventDefault()
                    this.handlePlusVolume();
                    break;
                case "arrowdown":
                    e.preventDefault()
                    this.handleMinusVolume();
                    break;
                case "m":
                    this.toggleMute();
                    break;
                default:
                    break;
            }
        },
        watchProgress() {
            if (this.isPlaying) {
                requestAnimationFrame(this.watchProgress);
                this.handleProgressBar();
            }
        },
        selectCurrentTime(e) {
            if (!this.focusOnTimeline) return
            const totalDurationRect = this.timelineElement.getBoundingClientRect();
            const width = Math.min(Math.max(0, e.clientX - totalDurationRect.x), totalDurationRect.width);
            const percentage = (width / totalDurationRect.width)
            this.currentTimelineElement.style.width = percentage * 100 + '%'
            this.currentTime = this.playerElement.currentTime  = percentage * this.playerElement.duration
            // TODO выводить время currentTime при движении мышкой над ней
        },
        setFocusOnTimeline(e) {
            this.isPlayingBeforeDrag = this.isPlaying
            this.pause()
            this.focusOnTimeline = true
            this.selectCurrentTime(e)
        },
        unsetFocusOnTimeline() {
            if (this.focusOnTimeline && this.isPlayingBeforeDrag) {
                this.play()
            }
            this.focusOnTimeline = false
        },
        toggleFullscreen() {
            if (!document.fullscreenElement) {
                
                if (this.isAudio) return
                if (this.videoSectionElement.requestFullscreen) {
                    this.videoSectionElement.requestFullscreen();
                } else if (this.videoSectionElement.webkitRequestFullscreen) { /* Safari */
                    this.videoSectionElement.webkitRequestFullscreen();
                } else if (this.videoSectionElement.msRequestFullscreen) { /* IE11 */
                    this.videoSectionElement.msRequestFullscreen();
                }
                this.fullscreenMode = true
            } else {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if (document.webkitExitFullscreen) { /* Safari */
                    document.webkitExitFullscreen();
                } else if (document.msExitFullscreen) { /* IE11 */
                    document.msExitFullscreen();
                }
                this.fullscreenMode = false
            }
        },
        onDocumentMouseMove(e) {
            this.selectVolume(e)
            this.selectCurrentTime(e)
        },
        onDocumentMouseUp(e) {
            this.unsetFocusOnVolume(e)
            this.unsetFocusOnTimeline(e)
        },
        togglePlaybackRateMenu() {
            this.isPlaybackRateMenuVisible = !this.isPlaybackRateMenuVisible;
        },
        showPlaybackRateMenu() {
            clearTimeout(this.playbackRateMenuTimeout);
            this.isPlaybackRateMenuVisible = true;
        },
        hidePlaybackRateMenu() {
            this.playbackRateMenuTimeout = setTimeout(() => {
                if (!this.isPlaybackRateMenuHovered) {
                    this.isPlaybackRateMenuVisible = false;
                }
            }, 300);
        },
        changePlaybackRate(rate) {
            this.currentPlaybackRate = rate;
            if (this.playerElement) {
                this.playerElement.playbackRate = rate;
            }
            this.hidePlaybackRateMenu();
        },
        isActiveRate(rate) {
            return Math.abs(this.currentPlaybackRate - rate) < 0.01;
        },
    },
    mounted() {
        this.playerElement = this.$refs['playerElement']
        this.videoSectionElement = this.$refs['videoSectionElement']
        this.timelineElement = this.$refs['timelineElement']
        this.currentTimelineElement = this.$refs['currentTimelineElement']
        this.bufferTimelineElement = this.$refs['bufferTimelineElement']
        this.volumeSliderElement = this.$refs['volumeSliderElement']
        this.playerElement.addEventListener("loadedmetadata", this.canInitPlayer);
        this.videoSectionElement.addEventListener("click", this.togglePlay)
        this.videoSectionElement.addEventListener("dblclick", this.toggleFullscreen)
        this.videoSectionElement.addEventListener("mousemove", this.showControls)
        // this.videoSectionElement.addEventListener("mouseleave", this.hideControls)
        this.playerElement.addEventListener("timeupdate", this.handleProgressBar)
        this.playerElement.addEventListener("progress", this.handleProgress);
        this.playerElement.addEventListener("waiting", () => {
            this.loading = true
        });
        this.playerElement.addEventListener("canplay", () => {
            this.loading = false
        });
        this.timelineElement.addEventListener('mousedown', this.setFocusOnTimeline)
        this.volumeSliderElement.addEventListener('mousedown', this.setFocusOnVolume);
        document.addEventListener('mouseup', this.onDocumentMouseUp);
        document.addEventListener('mousemove', this.onDocumentMouseMove);
        document.addEventListener("keydown", this.handleKeypress);
        if (this.poster) {
            this.playerElement.style.objectFit = 'cover'
        }
    },
    beforeDestroy() {
        this.playerElement.removeEventListener("loadedmetadata", this.canInitPlayer);
        this.videoSectionElement.removeEventListener("click", this.togglePlay)
        this.videoSectionElement.removeEventListener("dblclick", this.toggleFullscreen)
        this.playerElement.removeEventListener("timeupdate", this.handleProgressBar)
        this.timelineElement.removeEventListener('mousedown', this.setFocusOnTimeline)
        this.volumeSliderElement.removeEventListener('mousedown', this.setFocusOnVolume);
        clearTimeout(this.volumeSliderTimeout);
        document.removeEventListener('mouseup', this.onDocumentMouseUp);
        document.removeEventListener('mousemove', this.onDocumentMouseMove);
        document.removeEventListener("keydown", this.handleKeypress);
        clearTimeout(this.playbackRateMenuTimeout);
    },
    watch: {
        currentPlaybackRate(newRate) {
            if (this.playerElement) {
                this.playerElement.playbackRate = newRate;
            }
        },
        poster(val) {
            if (val) {
                this.playerElement.load()
                this.playerElement.style.objectFit = 'cover'
            }
        },
    }
}
</script>

<style lang="scss" scoped>
.section {
    display: flex;
    position: relative;
    width: 100%;
    max-width: 1000px;
    margin: 0 auto;
    &__cursor-show {
        cursor: pointer;
    }
    &__cursor-hide {
        cursor: none;
    }
}
.section .loader{
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%,-50%);
  width: 60px;
  height: 60px;
  border: 7px solid var(--accent-purple);
  border-bottom-color: transparent;
  border-radius: 50%;
  z-index: 1;
  animation: animate 0.6s linear infinite;
}
@keyframes animate{
  0%{
    transform: translate(-50%,-50%) rotate(0deg);
  }
  100%{
    transform: translate(-50%,-50%) rotate(360deg);
  }
}
.video {
    width: 100%;
    min-height: 300px;
    max-height: 600px;
    height: fit-content;
    background-color: rgba($color: $accent-purple, $alpha: .2);
    &--fullscreen {
        max-height: 100%;
        align-self: center;
    }
}
.controls {
    display: flex;
    width: 100%;
    position: absolute;
    bottom: 0px;
    user-select: none;
    gap: 5px;
    transition: opacity .2s;
    &--fullscreen {
        bottom: 0;
    }
    &--show {
        opacity: 1;
    }
    &--hide {
        opacity: 0;
    }
    .play-pause {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 44px;
        height: 44px;
        border-radius: 1px;
        background-color: $accent-purple;
        cursor: pointer;
        .play-pause-icon {
            width: 20px;
            height: 20px;
        }
    }
    .timeline {
        position: relative;
        background-color: $basic-grey;
        flex-grow: 1;
        transition: all 2s;
        .current {
            position: absolute;
            width: 0;
            height: 100%;
            background-color: $accent-purple;
            z-index: 10;
        }
        .buffer {
            position: absolute;
            width: 0;
            height: 100%;
            background-color: $dark-grey;
            z-index: 9;
            transition: width .2s;
        }
        .timer {
            position: absolute;
            left: 10px;
            z-index: 11;
            font-weight: 700;
            color: $another-grey;
            margin-top: 10px;
        }
    }
    .settings {
        display: flex;
        gap: 0px;
        position: relative;
        
        .button {
            display: flex;
            justify-content: center;
            align-items: center;
            min-width: 44px;
            height: 44px;
            border-radius: 1px;
            background-color: $accent-purple;
            cursor: pointer;
            position: relative;
            
            &.volume-button,
            &.playback-rate-button {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 4px;
                padding: 0 8px;
                min-width: 44px;
                
                .playback-rate-label {
                    color: white;
                    font-size: 12px;
                    font-weight: 500;
                }
            }
            
            .playback-rate-menu {
                position: absolute;
                bottom: calc(100% + 10px);
                right: -20px;
                width: 120px;
                background-color: rgba(0, 0, 0, 0.9);
                border-radius: 4px;
                padding: 8px 0;
                z-index: 10111;
                box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
                
                .playback-rate-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 8px 12px;
                    color: white;
                    cursor: pointer;
                    transition: background-color 0.2s;
                    
                    &:hover {
                        background-color: rgba(255, 255, 255, 0.1);
                    }
                    
                    &.active {
                        color: $accent-purple;
                        font-weight: 500;
                        
                        .check-icon {
                            opacity: 1;
                        }
                    }
                    
                    .playback-rate-value {
                        font-size: 13px;
                    }
                    
                    .check-icon {
                        font-size: 16px;
                        opacity: 0;
                        transition: opacity 0.2s;
                    }
                }
            }
        }
        .volume-container {
            position: absolute;
            bottom: calc(100% + 10px);
            width: 44px;
            height: 100px;
            background-color: rgba(0, 0, 0, 0.8);
            padding: 10px;
            border-radius: 4px;
            z-index: 100;
            cursor: default;
            
            .volume-slider {
                position: relative;
                width: 12px;
                height: 100%;
                background-color: $basic-grey;
                margin: 0 auto;
                cursor: pointer;
                border-radius: 3px;
                
                .volume-current {
                    position: absolute;
                    bottom: 0;
                    width: 100%;
                    height: 50%;
                    background-color: $accent-purple;
                    border-radius: 3px;
                    transition: height 0.1s;
                }
            }
        }
    }





}

// @media (max-width: 768px) {
//     .controls {
//         .settings {
//             .button {
//                 &.volume-button,
//                 &.playback-rate-button {
//                     width: 36px;
//                     height: 36px;
//                     min-width: 36px;
                    
//                     .playback-rate-label {
//                         font-size: 10px;
//                     }
//                 }
                
//                 .playback-rate-menu {
//                     width: 100px;
//                 }
                
//                 .volume-container {
//                     width: 36px;
//                     height: 80px;
//                     padding: 8px;
//                 }
//             }
//         }
//     }
// }

</style>